import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { createEvent, fetchCalendarEvents } from './outlook/calendar';
import { createGoogleCalendarEvent, fetchGoogleCalendar } from './google/calendar';
import { handleLogout, microsoftAuthService, msalInstance } from './outlook/config';
import { handleGoogleLogin, handleGoogleLogout, initGoogleClient } from './google/config';
import { AccountSelector, EventDetailsPopup, LoadingScreen, NewActivity, NoAuthScreen } from './widgets';
import { colors, dark_mode_primary, AppMode, AuthProvider, oAuthStatus, PaymentTiers, dark_mode_secondary, light_mode_secondary, light_mode_primary } from './values';

export function Calendar({ appMode, user, setUser, setAccounts, events, setEvents, error, setError, isMicrosoftLoggedIn, setIsMicrosoftLoggedIn, isGoogleLoggedIn, setIsGoogleLoggedIn, authManager, activeProviderAccounts, setActiveTab, subscriptionTier }) {
    const history = useHistory();
    const initializedRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const accounts = authManager.getLoggedInAccounts();//eslint-disable-next-line
    const [updateTrigger, setUpdateTrigger] = useState(0);
    const [activeAccount, setActiveAccount] = useState(null);
    const [selectedView, setSelectedView] = useState('week');
    const [isInitialized, setIsInitialized] = useState(false);
    const [fetchingEvents, setFetchingEvents] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [showAccountSelector, setShowAccountSelector] = useState(false);

    useEffect(() => {
        const handleAuthStateChange = () => {
            if (authManager) {
                setAccounts(authManager.getLoggedInAccounts());
                setUpdateTrigger(prev => prev + 1);
            }
        };
        window.addEventListener('authStateChanged', handleAuthStateChange);
        authManager.addStateChangeListener(handleAuthStateChange);
        return () => {
            window.removeEventListener('authStateChanged', handleAuthStateChange);
            authManager.removeStateChangeListener(handleAuthStateChange);
        };
    }, [setAccounts, authManager]);

    useEffect(() => {
        if (authManager) {
            const accounts = authManager.getLoggedInAccounts();
            setAccounts(accounts);
        }
    }, [setAccounts, authManager]);

    useEffect(() => {
        if (subscriptionTier !== undefined) {
            const accountsCount = authManager ? authManager.getLoggedInAccounts().length : 0;
            setSubscriptionInfo({
                tier: subscriptionTier,
                name: subscriptionTier === 0
                    ? 'Free'
                    : subscriptionTier === 1
                        ? 'Starter'
                        : subscriptionTier === 2
                            ? 'Pro'
                            : 'Enterprise',
                accountLimit: subscriptionTier === 0
                    ? 2
                    : subscriptionTier === 1
                        ? 5
                        : subscriptionTier === 2
                            ? 10
                            : 15,
                accountsUsed: accountsCount,
                canAddMore: accountsCount < (subscriptionTier === 0
                    ? 2
                    : subscriptionTier === 1
                        ? 5
                        : subscriptionTier === 2
                            ? 10
                            : 15
                ),
            });
        } else if (authManager) {
            try {
                if (typeof authManager.getSubscriptionInfo === 'function') {
                    setSubscriptionInfo(authManager.getSubscriptionInfo());
                } else {
                    const accountsCount = authManager.getLoggedInAccounts().length;
                    setSubscriptionInfo({
                        tier: PaymentTiers.FREE,
                        name: 'Free',
                        accountLimit: 2,
                        accountsUsed: accountsCount,
                        canAddMore: accountsCount < 2,
                    });
                }
            } catch (error) {
                setSubscriptionInfo({
                    tier: PaymentTiers.FREE,
                    name: 'Free',
                    accountLimit: 2,
                    accountsUsed: 0,
                    canAddMore: true,
                });
            }
        }//eslint-disable-next-line
    }, [subscriptionTier, updateTrigger]);

    useEffect(() => {
        if (!activeAccount && activeProviderAccounts) {
            const googleActive = activeProviderAccounts[AuthProvider.GOOGLE];
            const microsoftActive = activeProviderAccounts[AuthProvider.MICROSOFT];
            if (googleActive) {
                setActiveAccount(googleActive);
                if (!fetchingEvents) {
                    fetchEventsForAccount(googleActive);
                }
            } else if (microsoftActive) {
                setActiveAccount(microsoftActive);
                if (!fetchingEvents) {
                    fetchEventsForAccount(microsoftActive);
                }
            }
        }//eslint-disable-next-line
    }, []);

    const canAddMoreAccounts = useCallback(() => {
        if (!authManager) return true;
        if (subscriptionTier !== undefined) {
            const accountLimit = subscriptionTier === 0 ? 2 :
                subscriptionTier === 1 ? 5 :
                    subscriptionTier === 2 ? 10 : 15;
            return authManager.getLoggedInAccounts().length < accountLimit;
        }

        try {
            if (typeof authManager.canAddMoreAccounts === 'function') {
                return authManager.canAddMoreAccounts();
            } else {
                return authManager.getLoggedInAccounts().length < 2;
            }
        } catch (error) {
            return false;
        }//eslint-disable-next-line
    }, [subscriptionTier]);

    const handleUpgradeRedirect = () => {
        setShowUpgradePrompt(false);
        history.push('/pricing');
    };

    const refreshTokenIfNeeded = async (account) => {
        if (!account) return false;
        try {
            let accountId;
            if (typeof account === 'string') {
                accountId = account;
            } else if (account.id) {
                accountId = account.id;
            } else if (account.userData && account.userData.id) {
                accountId = account.userData.id;
            } else {
                const accounts = authManager.getAccounts();
                const matchingAccount = accounts.find(acc =>
                    acc.userData && acc.userData.username === account.username
                );
                if (matchingAccount) {
                    accountId = matchingAccount.id;
                } else {
                    return false;
                }
            }
            const tokenInfo = authManager.getTokenInfo(accountId);
            if (!tokenInfo) return false;
            const expiresInMinutes = (tokenInfo.expiresAt - Date.now()) / (1000 * 60);
            if (expiresInMinutes < 5) {
                if (account.provider === AuthProvider.MICROSOFT) {
                    await microsoftAuthService.getAccessToken(
                        accountId,
                        ['Calendars.Read', 'Calendars.ReadWrite', 'User.Read'],
                        true
                    );
                }
                return true;
            }
            return true;
        } catch (error) {
            return false;
        }
    };

    const fetchEventsForAccount = async (account) => {
        if (!account) return;
        setFetchingEvents(true);
        setLoading(true);
        setActiveAccount(account);
        setError(null);
        try {
            if (account.provider === AuthProvider.MICROSOFT) {
                await refreshTokenIfNeeded(account);
                await fetchCalendarEvents(account.userData || account, setLoading, setEvents, setError);
            } else if (account.provider === AuthProvider.GOOGLE) {
                await fetchGoogleCalendar(account.userData || account, setEvents, setError);
            }
        } catch (err) {
            setError(`Failed to fetch events: ${err.message}`);
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
            setFetchingEvents(false);
        }
    };

    const handleAccountSelect = async (account) => {
        try {
            if (!account || !account.provider) {
                throw new Error("Invalid account selected");
            }
            setLoading(true);
            setActiveAccount(account);
            const userData = account.userData || account;
            setUser(userData);
            if (authManager) {
                const accountId = account.id || `${account.provider}-${account.email || account.username}`;
                const storedAccount = authManager.getAccounts().find(acc => acc.id === accountId);
                if (storedAccount) {
                    authManager.setActiveAccount(storedAccount.id);
                }
            }
            await fetchEventsForAccount(account);
            setShowAccountSelector(false);
        } catch (err) {
            setError(`Failed to load events: ${err.message}`);
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveAccount = async (account) => {
        try {
            setLoading(true);
            if (!authManager) {
                throw new Error("Auth manager is required");
            }
            const accountId = account.id || `${account.provider}-${account.email || account.username}`;
            const storedAccount = authManager.getAccounts().find(acc => acc.id === accountId);

            if (!storedAccount) {
                throw new Error("Account not found");
            }
            let result = false;
            if (account.provider === AuthProvider.MICROSOFT) {
                result = await handleLogout(accountId, setUser, setLoading, setError, setIsMicrosoftLoggedIn);
            } else if (account.provider === AuthProvider.GOOGLE) {
                result = await handleGoogleLogout(authManager, accountId, setUser, setLoading, setError, setIsGoogleLoggedIn);
            }
            if (result) {
                if (authManager.getAccounts().some(acc => acc.id === accountId)) {
                    authManager.removeAccount(accountId);
                }
                try {
                    const savedAccounts = localStorage.getItem('AuthAccounts');
                    if (savedAccounts) {
                        const parsedAccounts = JSON.parse(savedAccounts);
                        const updatedAccounts = parsedAccounts.filter(acc => acc.id !== accountId);
                        if (parsedAccounts.length !== updatedAccounts.length) {
                            localStorage.setItem('AuthAccounts', JSON.stringify(updatedAccounts));
                        }
                    }
                    const activeAccounts = localStorage.getItem('ActiveAuthAccounts');
                    if (activeAccounts) {
                        const parsedActiveAccounts = JSON.parse(activeAccounts);
                        if (parsedActiveAccounts[account.provider] === accountId) {
                            const remainingAccounts = authManager.getAccounts().filter(
                                acc => acc.provider === account.provider && acc.id !== accountId
                            );

                            if (remainingAccounts.length > 0) {
                                parsedActiveAccounts[account.provider] = remainingAccounts[0].id;
                            } else {
                                delete parsedActiveAccounts[account.provider];
                            }
                            localStorage.setItem('ActiveAuthAccounts', JSON.stringify(parsedActiveAccounts));
                        }
                    }
                    const tokens = localStorage.getItem('AuthTokens');
                    if (tokens) {
                        const parsedTokens = JSON.parse(tokens);
                        if (parsedTokens[accountId]) {
                            delete parsedTokens[accountId];
                            localStorage.setItem('AuthTokens', JSON.stringify(parsedTokens));
                        }
                    }
                } catch (storageError) { }
                if (activeAccount &&
                    ((activeAccount.username === account.username && activeAccount.provider === account.provider) ||
                        (activeAccount.userData && activeAccount.userData.username === account.username && activeAccount.userData.provider === account.provider))) {

                    const remainingAccounts = authManager.getLoggedInAccounts();
                    if (remainingAccounts.length > 0) {
                        setTimeout(() => {
                            handleAccountSelect(remainingAccounts[0]);
                        }, 0);
                    } else {
                        setActiveAccount(null);
                        setEvents([]);
                    }
                }
                const remainingProviderAccounts = authManager.getAccounts(account.provider);
                if (remainingProviderAccounts.length === 0) {
                    if (account.provider === AuthProvider.GOOGLE) {
                        setIsGoogleLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                    } else if (account.provider === AuthProvider.MICROSOFT) {
                        setIsMicrosoftLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                    }
                }
                authManager.notifyStateChange();
                setUpdateTrigger(prev => prev + 1);
                setAccounts(authManager.getLoggedInAccounts());
            }
        } catch (error) {
            setError(`Failed to remove account: ${error.message}`);
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initializeAuth = async () => {
            if (initializedRef.current) return;
            try {
                setLoading(true);
                initializedRef.current = true;
                if (isMicrosoftLoggedIn === oAuthStatus.LOGGED_IN) {
                    try {
                        await microsoftAuthService.initialize();
                        const result = await msalInstance.handleRedirectPromise();
                        if (result) {
                            const newAccount = {
                                ...result.account,
                                provider: AuthProvider.MICROSOFT,
                                email: result.account.username,
                                username: result.account.username,
                                tenantId: result.account.tenantId || result.account.homeAccountId.split('.')[1]
                            };
                            setUser(newAccount);
                            if (authManager) {
                                try {
                                    if (!canAddMoreAccounts()) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    const existingAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                                    const existingAccount = existingAccounts.find(acc =>
                                        acc.userData && acc.userData.username === newAccount.username
                                    );

                                    let accountId;
                                    if (existingAccount) {
                                        authManager.updateUserData(existingAccount.id, newAccount);
                                        accountId = existingAccount.id;
                                    } else {
                                        const accountRecord = authManager.addAccount(AuthProvider.MICROSOFT, newAccount);
                                        accountId = accountRecord.id;
                                    }
                                    authManager.setActiveAccount(accountId);
                                    authManager.notifyStateChange();
                                    setUpdateTrigger(prev => prev + 1);
                                    setActiveAccount(newAccount);
                                    if (!fetchingEvents) {
                                        await fetchCalendarEvents(newAccount, setLoading, setEvents, setError);
                                    }
                                } catch (error) {
                                    if (error.message && error.message.includes("Account limit reached")) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    throw error;
                                }
                            } else {
                                setActiveAccount(newAccount);
                                setAccounts(prev => [...prev, newAccount]);
                                if (!fetchingEvents) {
                                    await fetchCalendarEvents(newAccount, setLoading, setEvents, setError);
                                }
                            }
                        } else {
                            const msAccounts = msalInstance.getAllAccounts();
                            if (msAccounts.length > 0) {
                                if (authManager) {
                                    const storedMsAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);

                                    if (storedMsAccounts.length === 0) {
                                        const primaryAccount = {
                                            ...msAccounts[0],
                                            provider: AuthProvider.MICROSOFT,
                                            email: msAccounts[0].username,
                                            username: msAccounts[0].username,
                                            tenantId: msAccounts[0].tenantId || msAccounts[0].homeAccountId.split('.')[1]
                                        };
                                        setUser(primaryAccount);

                                        try {
                                            if (!canAddMoreAccounts()) {
                                                setShowUpgradePrompt(true);
                                                return;
                                            }
                                            const accountRecord = authManager.addAccount(AuthProvider.MICROSOFT, primaryAccount);
                                            authManager.setActiveAccount(accountRecord.id);
                                            authManager.notifyStateChange();
                                            setUpdateTrigger(prev => prev + 1);
                                            setActiveAccount(primaryAccount);
                                            if (!fetchingEvents) {
                                                await fetchCalendarEvents(primaryAccount, setLoading, setEvents, setError);
                                            }
                                        } catch (error) {
                                            if (error.message && error.message.includes("Account limit reached")) {
                                                setShowUpgradePrompt(true);
                                                return;
                                            }
                                            throw error;
                                        }
                                    } else {
                                        const activeAccount = authManager.getActiveAccount(AuthProvider.MICROSOFT);
                                        if (activeAccount) {
                                            setUser(activeAccount.userData);
                                            setActiveAccount(activeAccount.userData);
                                            if (!fetchingEvents) {
                                                await fetchCalendarEvents(activeAccount.userData, setLoading, setEvents, setError);
                                            }
                                        } else if (storedMsAccounts.length > 0) {
                                            setUser(storedMsAccounts[0].userData);
                                            setActiveAccount(storedMsAccounts[0].userData);
                                            authManager.setActiveAccount(storedMsAccounts[0].id);
                                            if (!fetchingEvents) {
                                                await fetchCalendarEvents(storedMsAccounts[0].userData, setLoading, setEvents, setError);
                                            }
                                        }
                                    }
                                } else {
                                    const primaryAccount = {
                                        ...msAccounts[0],
                                        provider: AuthProvider.MICROSOFT,
                                        email: msAccounts[0].username,
                                        username: msAccounts[0].username,
                                        tenantId: msAccounts[0].tenantId || msAccounts[0].homeAccountId.split('.')[1]
                                    };
                                    msalInstance.setActiveAccount(msAccounts[0]);
                                    setUser(primaryAccount);
                                    setActiveAccount(primaryAccount);
                                    setAccounts(prev => [
                                        ...prev,
                                        primaryAccount
                                    ]);
                                    if (!fetchingEvents) {
                                        await fetchCalendarEvents(primaryAccount, setLoading, setEvents, setError);
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        setError(`Microsoft authentication error: ${error.message}`);
                        setShowErrorPopup(true);
                    }
                }
                if (isGoogleLoggedIn === oAuthStatus.LOGGED_IN && (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT)) {
                    try {
                        await initGoogleClient();
                        await new Promise(resolve => setTimeout(resolve, 500));
                        if (!window.gapi || !window.gapi.auth2) {
                            throw new Error("Google Auth not initialized properly");
                        }
                        const authInstance = window.gapi.auth2.getAuthInstance();
                        if (!authInstance) {
                            throw new Error("Failed to get Google Auth instance");
                        }
                        const googleUser = authInstance.currentUser.get();
                        if (googleUser && googleUser.isSignedIn()) {
                            const profile = googleUser.getBasicProfile();
                            const googleAccount = {
                                provider: AuthProvider.GOOGLE,
                                id: profile.getId(),
                                name: profile.getName(),
                                email: profile.getEmail(),
                                username: profile.getEmail(),
                                photoURL: profile.getImageUrl(),
                            };
                            setUser(googleAccount);
                            if (authManager) {
                                const storedGoogleAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
                                let accountExists = false;
                                let existingAccount = null;
                                for (const acc of storedGoogleAccounts) {
                                    if (acc.userData && acc.userData.email === googleAccount.email) {
                                        accountExists = true;
                                        existingAccount = acc;
                                        break;
                                    }
                                }
                                if (!accountExists) {
                                    try {
                                        if (!canAddMoreAccounts()) {
                                            setShowUpgradePrompt(true);
                                            return;
                                        }
                                        const accountRecord = authManager.addAccount(AuthProvider.GOOGLE, googleAccount);
                                        authManager.setActiveAccount(accountRecord.id);
                                        authManager.notifyStateChange();
                                        setUpdateTrigger(prev => prev + 1);
                                        if (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT) {
                                            setActiveAccount(googleAccount);
                                            if (!fetchingEvents) {
                                                await fetchGoogleCalendar(googleAccount, setEvents, setError);
                                            }
                                        }
                                    } catch (error) {
                                        if (error.message && error.message.includes("Account limit reached")) {
                                            setShowUpgradePrompt(true);
                                            return;
                                        }
                                        throw error;
                                    }
                                } else if (existingAccount) {
                                    authManager.updateUserData(existingAccount.id, googleAccount);
                                    if (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT) {
                                        authManager.setActiveAccount(existingAccount.id);
                                        setActiveAccount(googleAccount);
                                        if (!fetchingEvents) {
                                            await fetchGoogleCalendar(googleAccount, setEvents, setError);
                                        }
                                    }
                                }
                            } else {
                                setAccounts(prev => {
                                    const exists = prev.some(
                                        acc => acc.provider === AuthProvider.GOOGLE && acc.username === googleAccount.username
                                    );
                                    return exists ? prev : [...prev, googleAccount];
                                });
                                if (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT) {
                                    setActiveAccount(googleAccount);
                                    if (!fetchingEvents) {
                                        await fetchGoogleCalendar(googleAccount, setEvents, setError);
                                    }
                                }
                            }
                        } else {
                            if (authManager && (!activeAccount || activeAccount.provider !== AuthProvider.MICROSOFT)) {
                                try {
                                    if (!canAddMoreAccounts()) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    await handleGoogleLogin(authManager, setUser, setLoading, setError, setIsGoogleLoggedIn);
                                } catch (error) {
                                    if (error.message && error.message.includes("Account limit reached")) {
                                        setShowUpgradePrompt(true);
                                        return;
                                    }
                                    throw error;
                                }
                            }
                        }
                    } catch (error) {
                        if (error.message && error.message.includes("Account limit reached")) {
                            setShowUpgradePrompt(true);
                        } else {
                            setError(`Google account handling failed: ${error.message}`);
                            setShowErrorPopup(true);
                            setIsGoogleLoggedIn(oAuthStatus.NOT_LOGGED_IN);
                        }
                    }
                }
            } catch (err) {
                setError(`Authentication initialization failed: ${err.message}`);
                setShowErrorPopup(true);
            } finally {
                setIsInitialized(true);
                setLoading(false);
            }
        };
        if (!isInitialized && !initializedRef.current) {
            initializeAuth();
        }
    }, [isGoogleLoggedIn, setIsGoogleLoggedIn, isMicrosoftLoggedIn, setIsMicrosoftLoggedIn, setError, setEvents, setLoading, setUser, setAccounts, authManager, activeAccount, canAddMoreAccounts, isInitialized, fetchingEvents]);

    const getAccountInfo = (account) => {
        if (!account) return { email: 'No email', provider: AuthProvider.GOOGLE };
        const userData = account?.userData || account || {};
        const email = userData.email ||
            userData.username ||
            userData.userPrincipalName ||
            'No email';
        const provider = account?.provider ||
            (userData.provider) ||
            (email.includes('gmail') ? AuthProvider.GOOGLE : AuthProvider.MICROSOFT);
        return { email, provider };
    };

    const isAccountActive = (account) => {
        if (!activeAccount) return false;
        const accountInfo = getAccountInfo(account);
        const activeInfo = getAccountInfo(activeAccount);

        return accountInfo.provider === activeInfo.provider &&
            accountInfo.email === activeInfo.email;
    };

    const handleCreateEvent = async (eventData) => {
        try {
            setLoading(true);
            if (!activeAccount) {
                throw new Error("No active account selected");
            }
            if (activeAccount.provider === AuthProvider.MICROSOFT) {
                const newEvent = {
                    subject: eventData.title,
                    start: {
                        dateTime: eventData.start.toISOString(),
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    end: {
                        dateTime: eventData.end.toISOString(),
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    location: {
                        displayName: eventData.location || '',
                    },
                    body: {
                        contentType: "HTML",
                        content: eventData.description || '',
                    },
                    attendees: (eventData.attendees || []).map(a => ({
                        emailAddress: { address: a.email },
                        type: "required",
                    }))
                };

                await createEvent(newEvent, activeAccount, events, setEvents, setError);
            } else if (activeAccount.provider === AuthProvider.GOOGLE) {
                await createGoogleCalendarEvent(
                    activeAccount,
                    {
                        summary: eventData.title,
                        location: eventData.location || '',
                        description: eventData.description || '',
                        start: eventData.start,
                        end: eventData.end,
                        attendees: eventData.attendees || [],
                    },
                    setEvents,
                    setError,
                    events,
                );
            } else {
                throw new Error(`Unsupported provider: ${activeAccount.provider}`);
            }
            await fetchEventsForAccount(activeAccount);
        } catch (error) {
            setError(`Failed to create event: ${error.message}`);
            setShowErrorPopup(true);
        } finally {
            setLoading(false);
        }
    };
    if (!isInitialized || loading) {
        return <LoadingScreen appMode={appMode} />;
    }

    if (error && !showErrorPopup) {
        return (
            <div className="flex flex-col gap-4 justify-center items-center h-screen bg-gray-50">
                <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                    <div className="flex items-center mb-4 text-red-500">
                        <i className="bi bi-exclamation-circle text-xl mr-2"></i>
                        <h3 className="font-semibold text-lg">Error</h3>
                    </div>
                    <p className="font-medium text-gray-700">{error}</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setError(null)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition-colors">Dismiss</button>
                    <button onClick={() => window.location.reload()} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors">Retry</button>
                </div>
            </div>
        );
    }

    if (!user && (isGoogleLoggedIn === oAuthStatus.NOT_LOGGED_IN || isGoogleLoggedIn === oAuthStatus.NOT_DETERMINED) &&
        (isMicrosoftLoggedIn === oAuthStatus.NOT_LOGGED_IN || isMicrosoftLoggedIn === oAuthStatus.NOT_DETERMINED)) {
        return <NoAuthScreen appMode={appMode} setActiveTab={setActiveTab} />;
    }

    return (
        <div className={`min-h-screen flex flex-col items-center justify-center overflow-y-auto ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_primary}`}>
            <div className='w-full'>
                <div className="flex justify-center mb-4">
                    <div className={`p-4 rounded-lg shadow-md ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary}`}>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Selector</h3>
                            <button onClick={() => setShowAccountSelector(true)} className={`ml-4 px-3 py-1 rounded-md text-sm ${appMode === AppMode.LIGHT
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-blue-700 text-white hover:bg-blue-800'
                                } transition-colors`}>Manage Accounts
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {accounts.map((account, index) => {
                                const isActive = isAccountActive(account);
                                const accountInfo = getAccountInfo(account);
                                return (
                                    <button key={account.id || index} onClick={() => fetchEventsForAccount(account.userData || account)} className={`px-3 py-2 rounded-md text-sm ${isActive
                                        ? appMode === AppMode.LIGHT
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-blue-700 text-white'
                                        : appMode === AppMode.LIGHT
                                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            : 'bg-gray-700 text-white hover:bg-gray-600'
                                        } transition-colors`}>
                                        <div className="flex items-center">
                                            {accountInfo.provider === AuthProvider.MICROSOFT
                                                ? <i className="bi bi-microsoft mr-2 text-blue-400"></i>
                                                : <i className="bi bi-google mr-2 text-red-400"></i>
                                            }
                                            <span>{account.userData?.email || account.email || account.username}</span>
                                        </div>
                                    </button>
                                );
                            })}

                            {subscriptionInfo && subscriptionInfo.canAddMore && accounts.length < subscriptionInfo.accountLimit && (
                                <button onClick={() => setShowAccountSelector(true)} className={`px-3 py-2 rounded-md text-sm flex items-center ${appMode === AppMode.LIGHT ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-green-600 text-white hover:bg-green-700'} transition-colors`}>
                                    <i className="bi bi-plus-circle mr-2"></i>
                                    <span>Add Account</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <CalendarScreen appMode={appMode} events={events} selectedView={selectedView} setSelectedView={setSelectedView} provider={activeAccount ? activeAccount.provider : user.provider} onCreateEvent={handleCreateEvent} />
            </div>
            {showAccountSelector && <AccountSelector appMode={appMode} accounts={accounts} onClose={() => setShowAccountSelector(false)} onSelectAccount={handleAccountSelect} onRemoveAccount={handleRemoveAccount} activeAccount={activeAccount} subscriptionInfo={subscriptionInfo} onUpgrade={handleUpgradeRedirect} />}
            {showErrorPopup && error && (
                <div className={`flex flex-col gap-4 justify-center items-center h-screen ${appMode === AppMode.LIGHT ? light_mode_primary : dark_mode_primary}`}>
                    <div className={`${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary} p-8 rounded-lg shadow-lg max-w-md w-full`}>
                        <p className="font-medium text-gray-700">{error}</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setError(null)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition-colors">Dismiss</button>
                        <button onClick={() => window.location.reload()} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors">Retry</button>
                    </div>
                </div>
            )}
            {showUpgradePrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`w-96 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : 'bg-gray-800'}`}>
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Limit Reached</h2>
                            <button onClick={() => setShowUpgradePrompt(false)} className="text-gray-500 hover:text-gray-700">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-4">
                                    <i className="bi bi-exclamation-triangle text-2xl"></i>
                                </div>
                                <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Subscription Limit Reached</h3>
                                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>You've reached the maximum number of accounts for your current {subscriptionInfo?.name} plan.</p>
                            </div>
                            <div className="flex justify-center space-x-3">
                                <button onClick={() => setShowUpgradePrompt(false)} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-white'} transition-colors`}>Cancel</button>
                                <button onClick={handleUpgradeRedirect} className="px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-colors">Upgrade Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CalendarScreen({ appMode, events, selectedView, setSelectedView, provider, onCreateEvent, subscriptionTier }) {
    const [showPopup, setShowPopup] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showEventDetailsPopup, setShowEventDetailsPopup] = useState(false);

    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setShowEventDetailsPopup(true);
    };

    const normalizedEvents = events.map(event => {
        const isGoogleEvent = event.provider === AuthProvider.GOOGLE;
        const isAllDay = isGoogleEvent && event.isAllDay;
        return {
            id: event.id,
            subject: event.title || event.subject,
            startDateTime: event.startDateTime || event.startTime,
            endDateTime: event.endDateTime || event.endTime,
            startTime: isAllDay ? 'All day' : (typeof event.startTime === 'string' ? event.startTime :
                new Date(event.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
            endTime: isAllDay ? 'All day' : (typeof event.endTime === 'string' ? event.endTime :
                new Date(event.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
            location: event.location || 'No location',
            description: event.description || '',
            attendees: event.attendees || [],
            isAllDay: isAllDay,
            provider: event.provider || provider,
            htmlLink: event.htmlLink
        };
    });

    const generateDays = () => {
        const days = [];
        if (selectedView === 'day') {
            days.push(new Date(currentDate));
        } else if (selectedView === 'week') {
            const startOfWeek = new Date(currentDate);
            const day = currentDate.getDay();
            const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
            startOfWeek.setDate(diff);
            for (let i = 0; i < 7; i++) {
                const date = new Date(startOfWeek);
                date.setDate(startOfWeek.getDate() + i);
                days.push(date);
            }
        } else if (selectedView === 'month') {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            const daysInMonth = lastDay.getDate();
            const firstDayOfWeek = firstDay.getDay();
            const prevMonth = new Date(year, month, 0);
            const prevMonthDays = prevMonth.getDate();
            for (let i = firstDayOfWeek - 1; i >= 0; i--) {
                const date = new Date(year, month - 1, prevMonthDays - i);
                days.push(date);
            }
            for (let i = 1; i <= daysInMonth; i++) {
                const date = new Date(year, month, i);
                days.push(date);
            }
            const remainingCells = 42 - days.length;
            for (let i = 1; i <= remainingCells; i++) {
                const date = new Date(year, month + 1, i);
                days.push(date);
            }
        }
        return days;
    };

    const viewDays = generateDays();
    const getViewTitle = () => {
        if (selectedView === 'day') {
            return currentDate.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        } else if (selectedView === 'week') {
            return currentDate.toLocaleDateString('default', { month: 'long', year: 'numeric' });
        } else if (selectedView === 'month') {
            return currentDate.toLocaleDateString('default', { month: 'long', year: 'numeric' });
        }
    };

    const getFilteredEvents = () => {
        if (selectedView === 'day') {
            return normalizedEvents.filter(event => {
                const eventDate = new Date(event.startDateTime);//eslint-disable-next-line
                const isSameDay = (date1, date2) => {
                    return date1.getUTCFullYear() === date2.getUTCFullYear() &&
                        date1.getUTCMonth() === date2.getUTCMonth() &&
                        date1.getUTCDate() === date2.getUTCDate();
                };
                const currentDateStart = new Date(currentDate);
                currentDateStart.setHours(0, 0, 0, 0);
                const currentDateEnd = new Date(currentDate);
                currentDateEnd.setHours(23, 59, 59, 999);
                return (eventDate >= currentDateStart && eventDate <= currentDateEnd) ||
                    (event.endDateTime >= currentDateStart && event.startDateTime <= currentDateEnd);
            });
        } else if (selectedView === 'week') {
            const startOfWeek = new Date(viewDays[0]);
            startOfWeek.setHours(0, 0, 0, 0);

            const endOfWeek = new Date(viewDays[viewDays.length - 1]);
            endOfWeek.setHours(23, 59, 59, 999);

            return normalizedEvents.filter(event => {
                return (event.startDateTime >= startOfWeek && event.startDateTime <= endOfWeek) ||
                    (event.endDateTime >= startOfWeek && event.startDateTime <= endOfWeek);
            });
        } else if (selectedView === 'month') {
            return normalizedEvents.filter(event => {
                const eventDate = new Date(event.startDateTime);//eslint-disable-next-line
                const isSameDay = (date1, date2) => {
                    return date1.getUTCFullYear() === date2.getUTCFullYear() &&
                        date1.getUTCMonth() === date2.getUTCMonth() &&
                        date1.getUTCDate() === date2.getUTCDate();
                };
                const eventMonth = eventDate.getMonth();
                const eventYear = eventDate.getFullYear();
                return eventMonth === currentDate.getMonth() &&
                    eventYear === currentDate.getFullYear();
            });
        }
        return [];
    };

    const filteredEvents = getFilteredEvents();
    const groupEventsByDayAndHour = () => {
        const groupedEvents = {};
        viewDays.forEach(day => {
            const dayKey = day.toISOString().split('T')[0];
            groupedEvents[dayKey] = {};
            for (let hour = 0; hour <= 23; hour++) {
                groupedEvents[dayKey][hour] = [];
            }
        });

        filteredEvents.forEach(event => {
            if (event.isAllDay) {
                const eventStartDate = new Date(event.startDateTime);
                const dayKey = eventStartDate.toISOString().split('T')[0];
                if (groupedEvents[dayKey]) {
                    groupedEvents[dayKey][0].push(event);
                }
                return;
            }

            const eventStartDate = new Date(event.startDateTime);
            const eventEndDate = new Date(event.endDateTime);
            for (let day of viewDays) {
                const dayKey = day.toISOString().split('T')[0];
                const dayStart = new Date(day);
                dayStart.setHours(0, 0, 0, 0);
                const dayEnd = new Date(day);
                dayEnd.setHours(23, 59, 59, 999);

                if (eventStartDate <= dayEnd && eventEndDate >= dayStart) {
                    for (let hour = 0; hour <= 23; hour++) {
                        const hourStart = new Date(day);
                        hourStart.setHours(hour, 0, 0, 0);

                        const hourEnd = new Date(day);
                        hourEnd.setHours(hour, 59, 59, 999);
                        if (eventStartDate <= hourEnd && eventEndDate >= hourStart) {
                            if (groupedEvents[dayKey] && groupedEvents[dayKey][hour]) {
                                groupedEvents[dayKey][hour].push(event);
                            }
                        }
                    }
                }
            }
        });
        return groupedEvents;
    };

    const groupedEvents = groupEventsByDayAndHour();
    const groupEventsByDay = () => {
        const dayEvents = {};
        viewDays.forEach(day => {
            const dayStart = new Date(day);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(day);
            dayEnd.setHours(23, 59, 59, 999);
            const dayKey = day.toISOString().split('T')[0];
            dayEvents[dayKey] = [];
            filteredEvents.forEach(event => {
                const eventStart = new Date(event.startDateTime);
                const eventEnd = new Date(event.endDateTime);
                if ((eventStart >= dayStart && eventStart <= dayEnd) ||
                    (eventEnd >= dayStart && eventStart <= dayEnd)) {
                    dayEvents[dayKey].push(event);
                }
            });
        });
        return dayEvents;
    };

    const dayEvents = groupEventsByDay();
    const getEventColor = (subject) => {
        const hash = subject.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
    };

    const navigate = (direction) => {
        const newDate = new Date(currentDate);
        if (selectedView === 'day') {
            newDate.setDate(currentDate.getDate() + direction);
        } else if (selectedView === 'week') {
            newDate.setDate(currentDate.getDate() + (7 * direction));
        } else if (selectedView === 'month') {
            newDate.setMonth(currentDate.getMonth() + direction);
        }
        setCurrentDate(newDate);
    };

    const navigatePrev = () => navigate(-1);
    const navigateNext = () => navigate(1);
    const navigateToday = () => setCurrentDate(new Date());
    const hourRange = Array.from({ length: 24 }, (_, i) => i);
    const renderCalendarView = () => {
        if (selectedView === 'day') {
            return renderDayView();
        } else if (selectedView === 'week') {
            return renderWeekView();
        } else if (selectedView === 'month') {
            return renderMonthView();
        }
    };

    const renderDayView = () => {
        const today = currentDate.toISOString().split('T')[0];
        return (
            <div className="relative w-full">
                <div className="hidden sm:block w-full overflow-x-auto">
                    {hourRange.map((hour) => (
                        <div key={hour} className="grid grid-cols-1 w-full">
                            <div className={`h-32 lg:h-28 p-3.5 border-t border-r ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} flex items-start group transition-colors ${appMode === AppMode.LIGHT ? 'hover:bg-blue-50' : 'hover:bg-gray-800'}`}>
                                <span className={`text-xs font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-400 group-hover:text-gray-600' : 'text-gray-400 group-hover:text-gray-300'} mr-4 transition-colors`}>{hour === 0 ? '12:00 am' : hour === 12 ? '12:00 pm' : hour < 12 ? `${hour}:00 am` : `${hour - 12}:00 pm`}</span>
                                <div className="flex-1">
                                    {groupedEvents[today] && groupedEvents[today][hour] &&
                                        groupedEvents[today][hour].map((event, eventIndex) => {
                                            const colors = getEventColor(event.subject);
                                            return (
                                                <div onClick={() => handleEventClick(event)} key={eventIndex} className={`rounded-lg p-2 border-l-3 ${colors.border} ${colors.bg} mb-1.5 w-full shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer`}>
                                                    <p className="text-sm font-medium text-gray-900 mb-1 truncate">{event.subject}</p>
                                                    <div className="flex items-center">
                                                        <i className="bi bi-clock text-xs mr-1.5"></i>
                                                        <p className={`text-xs font-semibold ${colors.text}`}>{event.startTime} - {event.endTime}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderWeekView = () => {
        return (
            <div className="relative w-full">
                <div className={`grid grid-cols-8 border-t ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-gray-50' : 'border-gray-700 bg-gray-800'} sticky top-0 left-0 w-full shadow-sm z-10`}>
                    <div className="p-3.5 flex items-center justify-center text-sm font-medium text-gray-500"></div>
                    {viewDays.map((day, index) => {
                        const isToday = day.toDateString() === new Date().toDateString();
                        const formattedDate = `${day.toLocaleDateString('default', { weekday: 'short' })} ${day.getDate()}`;
                        return (
                            <div key={index} className={`p-3.5 flex items-center justify-center text-sm font-medium ${isToday
                                ? 'text-blue-600 font-bold'
                                : appMode === AppMode.LIGHT
                                    ? 'text-gray-700'
                                    : 'text-white'
                                }`}
                            >{formattedDate}
                            </div>
                        );
                    })}
                </div>

                <div className="hidden sm:block w-full overflow-x-auto">
                    {hourRange.map((hour) => (
                        <div key={hour} className="grid grid-cols-8 w-full">
                            <div className={`h-32 lg:h-28 p-0.5 md:p-3.5 border-t border-r ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'} flex items-center justify-end transition-all ${appMode === AppMode.LIGHT ? 'hover:bg-blue-50' : 'hover:bg-gray-800'} group`}>
                                <span className={`text-xs font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-400 group-hover:text-gray-600' : 'text-gray-400 group-hover:text-gray-300'} transition-colors`}>{hour === 12 ? `${hour}:00 pm` : hour > 12 ? `${hour - 12}:00 pm` : `${hour}:00 am`}</span>
                            </div>
                            {viewDays.map((day, dayIndex) => {
                                const dayKey = day.toISOString().split('T')[0];
                                const isToday = day.toDateString() === new Date().toDateString();
                                const hourEvents = groupedEvents[dayKey] && groupedEvents[dayKey][hour] ? groupedEvents[dayKey][hour] : [];
                                return (
                                    <div key={`${dayKey}-${hour}`} className={`h-32 lg:h-28 p-0.5 md:p-3.5 border-t ${dayIndex === 6 ? '' : 'border-r'} ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'
                                        } transition-all ${isToday
                                            ? appMode === AppMode.LIGHT
                                                ? 'bg-blue-50 hover:bg-blue-100'
                                                : 'bg-blue-900/20 hover:bg-blue-900/30'
                                            : appMode === AppMode.LIGHT
                                                ? 'hover:bg-gray-50'
                                                : 'hover:bg-gray-800'
                                        }`}
                                    >
                                        {hourEvents.map((event, eventIndex) => {
                                            const colors = getEventColor(event.subject);
                                            return (
                                                <div onClick={() => handleEventClick(event)} key={eventIndex} className={`rounded-lg p-2 border-l-3 ${colors.border} ${colors.bg} mb-1.5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer`}>
                                                    <p className="text-sm font-medium text-gray-900 mb-1 truncate">{event.subject}</p>
                                                    <div className="flex items-center">
                                                        <i className="bi bi-clock text-xs mr-1.5"></i>
                                                        <p className={`text-xs font-semibold ${colors.text}`}>{event.startTime} - {event.endTime}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                <div className="flex sm:hidden border-t border-gray-400 items-center w-full">
                    <div className="flex flex-col">
                        {hourRange.map((hour) => (
                            <div key={hour} className={`w-20 h-20 p-2 flex items-end text-xs font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-400 border-gray-200' : 'text-gray-400 border-gray-700'} border-b border-r`}>{hour === 12 ? `${hour}:00 pm` : hour > 12 ? `${hour - 12}:00 pm` : `${hour}:00 am`}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 w-full">
                        {hourRange.map((hour) => {
                            const today = new Date();
                            const todayKey = today.toISOString().split('T')[0];
                            const hourEvents = viewDays.some(day => day.toDateString() === today.toDateString()) &&
                                groupedEvents[todayKey] &&
                                groupedEvents[todayKey][hour] ?
                                groupedEvents[todayKey][hour] : [];

                            return (
                                <div key={hour} className={`w-full ${appMode === AppMode.LIGHT ? 'bg-white border-gray-200' : dark_mode_primary + ' border-gray-700'} h-20 border-b p-1.5`}>
                                    {hourEvents.length > 0 && hourEvents.map((event, eventIndex) => {
                                        const colors = getEventColor(event.subject);
                                        return (
                                            <div key={eventIndex} className={`w-full h-full rounded-lg p-2 border-l-3 ${colors.border} ${colors.bg} shadow-sm`}>
                                                <p className="text-sm font-medium text-gray-900 mb-0.5 truncate">{event.subject}</p>
                                                <div className="flex items-center">
                                                    <i className="bi bi-clock text-xs mr-1.5"></i>
                                                    <p className={`text-xs font-semibold ${colors.text}`}>{event.startTime} - {event.endTime}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const renderMonthView = () => {
        const weeks = [];
        for (let i = 0; i < viewDays.length; i += 7) { weeks.push(viewDays.slice(i, i + 7)); }
        return (
            <div className="w-full">
                <div className={`grid grid-cols-7 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200 bg-gray-50' : 'border-gray-700 bg-gray-800'}`}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((weekday, idx) => (
                        <div key={idx} className={`p-3 text-center text-sm font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-white'}`}>{weekday}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 w-full">
                    {viewDays.map((day, idx) => {
                        const dayKey = day.toISOString().split('T')[0];
                        const isToday = day.toDateString() === new Date().toDateString();
                        const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                        const events = dayEvents[dayKey] || [];
                        return (
                            <div key={idx} className={`min-h-24 p-1 border-b border-r ${appMode === AppMode.LIGHT
                                ? 'border-gray-200'
                                : 'border-gray-700'
                                } ${isCurrentMonth
                                    ? appMode === AppMode.LIGHT
                                        ? 'bg-white'
                                        : dark_mode_primary
                                    : appMode === AppMode.LIGHT
                                        ? 'bg-gray-50'
                                        : dark_mode_secondary
                                } ${isToday
                                    ? appMode === AppMode.LIGHT
                                        ? 'bg-blue-50'
                                        : 'bg-blue-900/20'
                                    : ''
                                } hover:shadow-inner transition-all duration-200`}>
                                <div className={`text-right p-1 ${isCurrentMonth
                                    ? appMode === AppMode.LIGHT
                                        ? 'text-gray-900'
                                        : 'text-white'
                                    : appMode === AppMode.LIGHT
                                        ? 'text-gray-400'
                                        : 'text-gray-400'
                                    } ${isToday
                                        ? 'font-bold text-blue-600'
                                        : ''
                                    }`}>{day.getDate()}
                                </div>
                                <div className="overflow-y-auto max-h-20">
                                    {events.slice(0, 3).map((event, eventIdx) => {
                                        const colors = getEventColor(event.subject);
                                        return (
                                            <div key={eventIdx} onClick={() => handleEventClick(event)} className={`text-xs p-1.5 mb-1 truncate rounded-md ${colors.bg} ${colors.text} border-l-3 ${colors.border} shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer`}>
                                                <span className="font-medium">{event.startTime}</span> {event.subject}
                                            </div>
                                        );
                                    })}
                                    {events.length > 3 && (
                                        <div className={`text-xs ${appMode === AppMode.LIGHT
                                            ? 'text-gray-500 bg-gray-100'
                                            : 'text-gray-300 bg-gray-700'
                                            } px-1.5 py-1 rounded-md text-center font-medium`}>+{events.length - 3} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const newActivity = async () => { setShowPopup(true); };

    return (
        <section className={`${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary} py-8 w-full rounded-xl shadow-lg`}>
            <h1 className={`text-2xl font-bold mb-8 text-center ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Calendar Events</h1>
            <div className="w-full max-w-7xl mx-auto px-6 lg:px-8 overflow-x-auto">
                <div className="flex flex-col md:flex-row max-md:gap-4 items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button onClick={navigatePrev} className={`w-10 h-10 rounded-full flex items-center justify-center ${appMode === AppMode.LIGHT
                            ? 'text-gray-500 hover:bg-gray-100'
                            : 'text-white hover:bg-gray-800'} transition-colors`}>
                            <i className="bi bi-chevron-left text-xl"></i>
                        </button>
                        <button onClick={navigateToday} className={`px-4 py-2 border rounded-lg text-sm font-medium ${appMode === AppMode.LIGHT
                            ? 'border-gray-300 hover:bg-gray-100 text-gray-700'
                            : 'border-gray-600 hover:bg-gray-800 text-white'
                            } transition-colors`}
                        >Today
                        </button>
                        <h6 className={`text-xl leading-8 font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>{getViewTitle()}</h6>
                        <button onClick={navigateNext} className={`w-10 h-10 rounded-full flex items-center justify-center ${appMode === AppMode.LIGHT
                            ? 'text-gray-500 hover:bg-gray-100'
                            : 'text-white hover:bg-gray-800'
                            } transition-colors`}
                        >
                            <i className="bi bi-chevron-right text-xl"></i>
                        </button>
                    </div>
                    <div className={`flex items-center rounded-xl ${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'border border-gray-600'} p-1`}>
                        <button className={`rounded-lg py-2.5 px-5 text-sm font-medium ${selectedView === 'day'
                            ? `text-blue-600 ${appMode === AppMode.LIGHT
                                ? 'bg-white shadow-sm hover:bg-gray-50'
                                : dark_mode_secondary + ' shadow-sm hover:bg-gray-800'
                            }`
                            : appMode === AppMode.LIGHT
                                ? 'text-gray-600 hover:text-gray-800'
                                : 'text-gray-200 hover:text-white'
                            } transition-all duration-300`} onClick={() => setSelectedView('day')}
                        >Day
                        </button>
                        <button className={`rounded-lg py-2.5 px-5 text-sm font-medium ${selectedView === 'week'
                            ? `text-blue-600 ${appMode === AppMode.LIGHT
                                ? 'bg-white shadow-sm hover:bg-gray-50'
                                : dark_mode_secondary + ' shadow-sm hover:bg-gray-800'
                            }`
                            : appMode === AppMode.LIGHT
                                ? 'text-gray-600 hover:text-gray-800'
                                : 'text-gray-200 hover:text-white'
                            } transition-all duration-300`} onClick={() => setSelectedView('week')}
                        >Week
                        </button>
                        <button className={`rounded-lg py-2.5 px-5 text-sm font-medium ${selectedView === 'month'
                            ? `text-blue-600 ${appMode === AppMode.LIGHT
                                ? 'bg-white shadow-sm hover:bg-gray-50'
                                : dark_mode_secondary + ' shadow-sm hover:bg-gray-800'
                            }`
                            : appMode === AppMode.LIGHT
                                ? 'text-gray-600 hover:text-gray-800'
                                : 'text-gray-200 hover:text-white'
                            } transition-all duration-300`}
                            onClick={() => setSelectedView('month')}
                        >Month
                        </button>
                    </div>
                    <button onClick={newActivity} className="py-2.5 px-5 bg-blue-600 rounded-lg text-sm font-medium text-white transition-all duration-200 hover:bg-blue-700 shadow-sm flex items-center gap-2">
                        <i className="bi bi-plus-lg"></i> New Event
                    </button>
                </div>{renderCalendarView()}
            </div>
            {showPopup && <NewActivity appMode={appMode} onClose={() => setShowPopup(false)} onSave={onCreateEvent} />}
            {showEventDetailsPopup && selectedEvent && <EventDetailsPopup event={selectedEvent} appMode={appMode} onClose={() => setShowEventDetailsPopup(false)} />}
        </section>
    );
}